import { useCallback, useEffect, useRef } from "react";

import { useMap } from "@vis.gl/react-google-maps";
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";

import ClusterPin from "./NitroClusterPin";

import { useIsComponentMounted } from "../../../hooks/useIsComponentMounted";
import { useNitroStore } from "../../../store/Store";
import { createNitroMarker } from "./NitroMarker";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { IPinExtension } from "../../../types/ILocation";

const LocationsLayer = ({ locations }: { locations: IPinExtension[] }) => {
  const isMobile = useIsMobile(768);
  const [
    labelType,
    selectedLocation,
    setSelectedLocation,
    setSelectedLocationLatLng,
    setShowMapPopup,
    hideLocationPins,
    product,
    mode,
    partyTypes,
    hidePins,
  ] = useNitroStore((state) => [
    state.labelType,
    state.selectedLocation,
    state.setSelectedLocation,
    state.setSelectedLocationLatLng,
    state.setShowMapPopup,
    state.hideLocationPins,
    state.product,
    state.mode,
    state.partyTypes,
    state.hidePins,
  ]);

  const checkPinDisabled = (r: IPinExtension) => {
    if (r.pinType === "zipCodes") return false;

    const productCheck = product && r.products?.indexOf(product) === -1;
    const partyTypeCheck =
      r.pinType === "kochAndCompetitor"
        ? partyTypes.indexOf("koch") === -1 &&
          partyTypes.indexOf("competitor") === -1
        : partyTypes.indexOf(r.pinType) === -1;

    const isDisabled = productCheck || partyTypeCheck;

    return isDisabled;
  };

  const isComponentMounted = useIsComponentMounted();
  const map = useMap();

  const listeners = useRef<google.maps.MapsEventListener[]>([]);
  const lastAutoFitKeyRef = useRef<string>("");

  const kochClusterer = useRef<MarkerClusterer | null>(null);
  const kochDisabledClusterer = useRef<MarkerClusterer | null>(null);
  const customerClusterer = useRef<MarkerClusterer | null>(null);
  const customerDisabledClusterer = useRef<MarkerClusterer | null>(null);
  const competitorClusterer = useRef<MarkerClusterer | null>(null);
  const competitorDisabledClusterer = useRef<MarkerClusterer | null>(null);
  const kochAndCompetitorClusterer = useRef<MarkerClusterer | null>(null);
  const kochAndCompetitorDisabledClusterer = useRef<MarkerClusterer | null>(
    null,
  );
  const zipCodesClusterer = useRef<MarkerClusterer | null>(null);

  const createClusterer = useCallback(() => {
    zipCodesClusterer.current = new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({ radius: 100 }),

      renderer: {
        render: ({ count, position }) => {
          return new google.maps.marker.AdvancedMarkerElement({
            title: String("zipCodes"),
            position,
            content: ClusterPin("zipCodes", count),
          });
        },
      },
    });

    kochClusterer.current = new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({ radius: 100 }),

      renderer: {
        render: ({ count, position }) => {
          return new google.maps.marker.AdvancedMarkerElement({
            title: String("Koch"),
            position,
            content: ClusterPin("koch", count, false),
          });
        },
      },
    });

    kochDisabledClusterer.current = new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({ radius: 100 }),

      renderer: {
        render: ({ count, position }) => {
          return new google.maps.marker.AdvancedMarkerElement({
            title: String("Koch"),
            position,
            content: ClusterPin("koch", count, true),
          });
        },
      },
    });

    competitorClusterer.current = new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({ radius: 100 }),

      renderer: {
        render: ({ count, position }) => {
          return new google.maps.marker.AdvancedMarkerElement({
            title: String("Competitor"),
            position,
            content: ClusterPin("competitor", count, false),
          });
        },
      },
    });
    competitorDisabledClusterer.current = new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({ radius: 100 }),

      renderer: {
        render: ({ count, position }) => {
          return new google.maps.marker.AdvancedMarkerElement({
            title: String("Competitor"),
            position,
            content: ClusterPin("competitor", count, true),
          });
        },
      },
    });

    customerClusterer.current = new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({ radius: 100 }),
      renderer: {
        render: ({ count, position }) => {
          return new google.maps.marker.AdvancedMarkerElement({
            title: String("Customer"),
            position,
            content: ClusterPin("customer", count, false),
          });
        },
      },
    });
    customerDisabledClusterer.current = new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({ radius: 100 }),
      renderer: {
        render: ({ count, position }) => {
          return new google.maps.marker.AdvancedMarkerElement({
            title: String("Customer"),
            position,
            content: ClusterPin("customer", count, true),
          });
        },
      },
    });

    kochAndCompetitorClusterer.current = new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({ radius: 100 }),

      renderer: {
        render: ({ count, position }) => {
          return new google.maps.marker.AdvancedMarkerElement({
            title: String("Koch and Competitor"),
            position,
            content: ClusterPin("kochAndCompetitor", count, false),
          });
        },
      },
    });
    kochAndCompetitorDisabledClusterer.current = new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({ radius: 100 }),

      renderer: {
        render: ({ count, position }) => {
          return new google.maps.marker.AdvancedMarkerElement({
            title: String("Koch and Competitor"),
            position,
            content: ClusterPin("kochAndCompetitor", count, true),
          });
        },
      },
    });
  }, [map]);

  const getClusterer = (pinType: string, disabled: boolean) => {
    if (pinType === "koch") {
      if (disabled) {
        return kochDisabledClusterer.current;
      }
      return kochClusterer.current;
    }
    if (pinType === "customer") {
      if (disabled) {
        return customerDisabledClusterer.current;
      }
      return customerClusterer.current;
    }
    if (pinType === "competitor") {
      if (disabled) {
        return competitorDisabledClusterer.current;
      }
      return competitorClusterer.current;
    }
    if (pinType === "zipCodes") {
      return zipCodesClusterer.current;
    }
    if (pinType === "kochAndCompetitor") {
      if (disabled) {
        return kochAndCompetitorDisabledClusterer.current;
      }
      return kochAndCompetitorClusterer.current;
    }
    return null;
  };

  const isModeEnabledForLocation = useCallback(
    (location: IPinExtension, transportMode: "TRUCK" | "RAIL") => {
      const rawValue =
        transportMode === "RAIL" ? location.railEnabled : location.truckEnabled;
      return Boolean(rawValue);
    },
    [],
  );

  const createMarker = (
    AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement,
    l: IPinExtension,
    hovering?: boolean,
    selected?: boolean,
    disabled?: boolean,
    m?: any,
  ) => {
    const showAsToken = (showAs?: string) => {
      if (showAs === "DISCUSS") return "DISC";
      if (showAs === "NO_OFFER") return "N/O";
      return null;
    };

    const toMoneyText = (value: number | null | undefined) =>
      value !== null && value !== undefined ? `$${value}` : "";

    const buildKochModeLabel = (
      postedShowAs?: string,
      postedPrice?: number | null,
      modeShowAs?: string,
      modeAltPrice?: number | null,
    ) => {
      const postedToken = showAsToken(postedShowAs);
      const modeToken = showAsToken(modeShowAs);
      const postedText = postedToken || toMoneyText(postedPrice);
      const modeText = modeToken || toMoneyText(modeAltPrice);

      const hasPostedNumber = !postedToken && postedText !== "";
      const hasModeNumber = !modeToken && modeText !== "";
      const hasPostedToken = Boolean(postedToken);
      const hasModeToken = Boolean(modeToken);

      if (
        (hasPostedToken && hasModeNumber) ||
        (hasPostedNumber && hasModeToken)
      ) {
        return `${postedText}-${modeText}`;
      }

      if (hasPostedNumber && hasModeNumber) {
        return `${postedText}-${modeText}`;
      }

      if (postedText && modeText) {
        return `${postedText}-${modeText}`;
      }

      return postedText || modeText || "";
    };

    let label = "";

    if (l.pinType === "zipCodes") {
      label = l.id;
    } else if (labelType === "prices") {
      const values = [];
      if (l.name) {
        values.push(l.name);
      }

      if (l.price) {
        values.push(`$${l.price}`);
      }

      label = values.join(" - ");
    } else if (labelType === "city") {
      label = l.city;
    } else if (labelType === "facility") {
      label = String(l.owner || "Unknown facility owner");
    } else if (labelType === "location") {
      // label = String(l.name.split("-")[0]);
      label = String(l.name);
    }

    // For favorite origins on mobile with price data, show custom label
    if (isMobile) {
      if (l.pinType === "koch" || l.pinType === "competitor") {
        if (l.pinType === "koch") {
          if (mode === "RAIL") {
            label = buildKochModeLabel(
              l.postedShowAs,
              l.postedPrice,
              l.railShowAs,
              l.railMinPrice,
            );
          } else {
            label = buildKochModeLabel(
              l.postedShowAs,
              l.postedPrice,
              l.truckShowAs,
              l.truckMinPrice,
            );
          }
        } else {
          // competitor — use mode-specific price
          const activeShowAs = mode === "RAIL" ? l.railShowAs : l.truckShowAs;
          const activePrice =
            mode === "RAIL" ? l.railMaxPrice : l.truckMaxPrice;
          if (activeShowAs === "NO_OFFER") {
            label = "No Offer";
          } else {
            label =
              activePrice !== null && activePrice !== undefined
                ? `$${activePrice}`
                : "";
          }
        }
      } else {
        let currentMin = null;
        let currentMax = null;

        if (mode === "RAIL") {
          currentMin = l.railMinPrice;
          currentMax = l.railMaxPrice;
        } else {
          currentMin = l.truckMinPrice;
          currentMax = l.truckMaxPrice;
        }

        const hasMin = currentMin !== null && currentMin !== undefined;
        const hasMax = currentMax !== null && currentMax !== undefined;

        if (hasMin && hasMax) {
          label = `$${currentMax}-$${currentMin}`;
        } else if (hasMax && !hasMin) {
          label = `$${currentMax}`;
        } else if (!hasMax && hasMin) {
          label = `$${currentMin}`;
        } else {
          label = "";
        }
      }
    }

    const content = createNitroMarker(
      l.pinType,
      label,
      l.id,
      hovering,
      selected,
      disabled,
    );

    const marker = new AdvancedMarkerElement({
      position: { lat: l.lat, lng: l.long },
      content,
      map: m,
    });

    return marker;
  };

  useEffect(() => {
    if (!isComponentMounted) return;
    if (!map) return;
    if (!kochClusterer.current) {
      createClusterer();
    }
  }, [isComponentMounted, map, createClusterer]);

  const load = useCallback(async () => {
    const { AdvancedMarkerElement } = (await google.maps.importLibrary(
      "marker",
    )) as google.maps.MarkerLibrary;

    const sortLocations = [
      ...locations.filter((l) => l.pinType === "zipCodes"),
      ...locations.filter((l) => l.pinType === "customer"),
      ...locations.filter((l) => l.pinType === "competitor"),
      ...locations.filter((l) => l.pinType === "kochAndCompetitor"),
      ...locations.filter((l) => l.pinType === "koch"),
    ];

    const renderStats = {
      total: 0,
      willRender: 0,
      hiddenByNoModes: 0,
      hiddenByDisabledAndHidePins: 0,
      byType: {
        koch: { total: 0, willRender: 0 },
        competitor: { total: 0, willRender: 0 },
        customer: { total: 0, willRender: 0 },
        kochAndCompetitor: { total: 0, willRender: 0 },
        zipCodes: { total: 0, willRender: 0 },
      } as Record<string, { total: number; willRender: number }>,
    };
    const visibleLocationsForBounds: Array<{
      id: string;
      lat: number;
      long: number;
    }> = [];

    sortLocations.forEach((l) => {
      renderStats.total += 1;
      if (!renderStats.byType[l.pinType]) {
        renderStats.byType[l.pinType] = { total: 0, willRender: 0 };
      }
      renderStats.byType[l.pinType].total += 1;

      const disabled = checkPinDisabled(l);
      const cluster = getClusterer(l.pinType, disabled);

      if (!cluster) {
        return;
      }

      const matching = locations.find((r) => {
        return l.lat === r.lat && l.long === r.long;
      });

      if (matching) {
        // eslint-disable-next-line no-param-reassign
        l.lat += +Math.random() * 0.00015;
        // eslint-disable-next-line no-param-reassign
        l.long += +Math.random() * 0.0001;
      }

      let hideMobilePin = false;
      const railEnabled = isModeEnabledForLocation(l, "RAIL");
      const truckEnabled = isModeEnabledForLocation(l, "TRUCK");
      const isCurrentModeEnabled = mode === "RAIL" ? railEnabled : truckEnabled;

      if (
        isMobile &&
        ((!railEnabled && !truckEnabled) || !isCurrentModeEnabled)
      ) {
        hideMobilePin = true;
        renderStats.hiddenByNoModes += 1;
      }

      const willRender = !(disabled && hidePins) && !hideMobilePin;
      if (disabled && hidePins) {
        renderStats.hiddenByDisabledAndHidePins += 1;
      }

      if (willRender) {
        renderStats.willRender += 1;
        renderStats.byType[l.pinType].willRender += 1;
        visibleLocationsForBounds.push({
          id: l.id,
          lat: l.lat,
          long: l.long,
        });

        const marker = createMarker(
          AdvancedMarkerElement,
          l,
          false,
          l.id === selectedLocation?.id,
          disabled,
        );

        const listener = marker.addListener("click", ({ latLng }: any) => {
          setSelectedLocation(l);
          setSelectedLocationLatLng({ lat: latLng.lat(), lng: latLng.lng() });
          setShowMapPopup(true);
        });

        listeners.current.push(listener);

        cluster.addMarker(marker);
      }
    });

    if (isMobile && visibleLocationsForBounds.length > 0 && map) {
      const autoFitKey = visibleLocationsForBounds
        .map((location) => location.id)
        .sort()
        .join("|");

      if (lastAutoFitKeyRef.current !== autoFitKey) {
        const bounds = new google.maps.LatLngBounds();
        visibleLocationsForBounds.forEach((location) => {
          bounds.extend({ lat: location.lat, lng: location.long });
        });

        map.fitBounds(bounds, {
          top: 80,
          right: 120,
          bottom: 180,
          left: 24,
        });

        lastAutoFitKeyRef.current = autoFitKey;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    product,
    mode,
    partyTypes,
    locations,
    labelType,
    selectedLocation,
    setSelectedLocation,
    setShowMapPopup,
    hidePins,
    isModeEnabledForLocation,
  ]);

  useEffect(() => {
    if (!isComponentMounted) return;
    if (!map) {
      return;
    }
    if (listeners.current && listeners.current.length > 0) {
      listeners.current.forEach((l) => l.remove());
      listeners.current = [];
    }
    kochClusterer.current?.clearMarkers();
    kochDisabledClusterer.current?.clearMarkers();
    customerClusterer.current?.clearMarkers();
    customerDisabledClusterer.current?.clearMarkers();
    competitorClusterer.current?.clearMarkers();
    competitorDisabledClusterer.current?.clearMarkers();
    kochAndCompetitorClusterer.current?.clearMarkers();
    kochAndCompetitorDisabledClusterer.current?.clearMarkers();
    zipCodesClusterer.current?.clearMarkers();

    if (hideLocationPins) {
      return;
    }

    if (locations.length > 0) {
      load();
    }
  }, [
    createClusterer,
    isComponentMounted,
    load,
    map,
    hideLocationPins,
    isMobile,
    product,
    mode,
    partyTypes,
    locations,
    labelType,
    selectedLocation,
    setSelectedLocation,
    setShowMapPopup,
  ]);

  return null;
};

export default LocationsLayer;
